import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Briefcase, Shield } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "./types";

interface OccupationAndTFNFieldsProps {
  form: UseFormReturn<FormValues>;
  isEditing: boolean;
}

export function OccupationAndTFNFields({ form, isEditing }: OccupationAndTFNFieldsProps) {
  const currentOccupation = form.watch('occupation');
  const currentTFN = form.watch('tfn');

  if (!isEditing) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="transition-all duration-200 hover:shadow-md">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Briefcase className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Occupation</p>
                <p className="text-sm font-medium">{currentOccupation || "Not set"}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="transition-all duration-200 hover:shadow-md">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Tax File Number</p>
                <p className="text-sm font-medium">{currentTFN || "Not set"}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="transition-all duration-200">
        <div className="p-6">
          <FormField
            control={form.control}
            name="occupation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Occupation</FormLabel>
                <FormControl>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Input 
                      placeholder="Enter your occupation"
                      className="pl-10"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Card>

      <Card className="transition-all duration-200">
        <div className="p-6">
          <FormField
            control={form.control}
            name="tfn"
            rules={{
              pattern: {
                value: /^\d{9}$/,
                message: "TFN must be exactly 9 digits"
              }
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax File Number (TFN)</FormLabel>
                <FormControl>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Input 
                      placeholder="Enter your 9-digit TFN"
                      className="pl-10"
                      maxLength={9}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 9) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage />
                <p className="text-sm text-muted-foreground">
                  Your TFN is securely stored and encrypted
                </p>
              </FormItem>
            )}
          />
        </div>
      </Card>
    </div>
  );
}